// ============================================================================
// useNearViewport — «элемент близко к вьюпорту»: ленивое монтирование
// тяжёлых медиа в ленте (§2.4). Плеер создаётся ТОЛЬКО у видимых и
// приближающихся записей; до этого — лёгкий плейсхолдер (MediaPlaceholder).
// Инварианты:
//   • срабатывание НЕОБРАТИМО — начавшееся воспроизведение не прерывается
//     прокруткой (виртуализация не выгружает уже смонтированный плеер);
//   • setState — только в колбэке IntersectionObserver (не в теле эффекта);
//   • root=null — пересечение считается и внутри прокручиваемого контейнера
//     Virtuoso (наблюдать за скроллером отдельно не нужно).
// Компонент-потребитель добавляет свои необратимые условия активности
// (клик по заглушке, автозапуск очереди) — см. chatAudioAttachment.
// ============================================================================

import { useEffect, useRef, useState, type RefObject } from 'react';

/** Запас до вьюпорта: плеер монтируется заранее — к показу уже готов. */
const DEFAULT_MARGIN = '240px';

export function useNearViewport(margin = DEFAULT_MARGIN): {
  /** Контейнер наблюдаемого элемента (заглушка и плеер живут в нём). */
  ref: RefObject<HTMLDivElement | null>;
  /** true — элемент был/находится близко к вьюпорту (необратимо). */
  near: boolean;
} {
  const ref = useRef<HTMLDivElement | null>(null);
  // Капабилити-проверка среды: без IntersectionObserver ленивость невозможна —
  // содержимое показывается сразу (не фоллбэк-заглушка).
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    if (near) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) setNear(true);
    }, { rootMargin: margin });
    observer.observe(el);
    return () => observer.disconnect();
  }, [near, margin]);

  return { ref, near };
}
